import axios, { AxiosResponse } from 'axios';

import {
  Folder,
  FolderGetDTO,
  FolderPostDTO,
  TestCaseInFolder,
} from '@qality/common-model';
import {
  FolderUpdateDTO,
  FolderWithParent,
} from '@qality/common-model/src/Folder';

import StorageOfUniqueServices from '../StorageOfUniqueServices';

const createFolder = async (folderData: FolderPostDTO): Promise<Folder> => {
  const url = await StorageOfUniqueServices.get(
    'AuthenticateService'
  ).authenticateByJWT(
    StorageOfUniqueServices.get('WindowOriginService').origin +
      StorageOfUniqueServices.get('APIConstants').URL_FOLDERS
  );

  const { data: newFolder } = await axios.post(url, folderData, {
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return parseFolderGetDto(newFolder);
};

const updateFolder = async (folderData: FolderUpdateDTO): Promise<Folder> => {
  const url = await StorageOfUniqueServices.get(
    'AuthenticateService'
  ).authenticateByJWT(
    StorageOfUniqueServices.get('WindowOriginService').origin +
      StorageOfUniqueServices.get('APIConstants').URL_FOLDERS +
      `/${folderData.id}`
  );

  const data = {
    fields: {
      ...(folderData?.name && { name: folderData.name }),
      ...(folderData?.parentId && {
        parentId: folderData.parentId,
      }),
    },
  };

  return await axios.patch(url, data, {
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

const deleteFolder = async (
  folderId: number
): Promise<AxiosResponse<string>> => {
  const url = await StorageOfUniqueServices.get(
    'AuthenticateService'
  ).authenticateByJWT(
    StorageOfUniqueServices.get('WindowOriginService').origin +
      StorageOfUniqueServices.get('APIConstants').URL_FOLDERS +
      `/${folderId}`
  );

  return axios.delete(url);
};

const getRootFolders = async (): Promise<Array<Folder>> => {
  const params = {
    onlyRootFolders: true,
  };

  const url = await StorageOfUniqueServices.get(
    'AuthenticateService'
  ).authenticateByJWT(
    StorageOfUniqueServices.get('WindowOriginService').origin +
      StorageOfUniqueServices.get('APIConstants').URL_FOLDERS,
    Object.entries(params).map(([key, value]) => `${key}=${value}`)
  );

  const { data: newFolders } = await axios.get<Array<FolderGetDTO>>(url, {
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return parseFolderGetDtos(newFolders);
};

const getFolder = async (
  id: Folder['id']
): Promise<{ newFolder: Folder; newSubfolders: Array<Folder> }> => {
  const url = await StorageOfUniqueServices.get(
    'AuthenticateService'
  ).authenticateByJWT(
    StorageOfUniqueServices.get('WindowOriginService').origin +
      StorageOfUniqueServices.get('APIConstants').URL_FOLDERS +
      `/${id}`
  );

  const { data: newFolder } = await axios.get<FolderGetDTO>(url, {
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return {
    newFolder: parseFolderGetDto(newFolder),
    newSubfolders: parseFolderGetDtos(newFolder.subfolders),
  };
};

const addTestCasesToFolder = async (
  testCasesIds: Array<number | string>,
  folderId: number
): Promise<AxiosResponse<Array<TestCaseInFolder>>> => {
  const url = await StorageOfUniqueServices.get(
    'AuthenticateService'
  ).authenticateByJWT(
    StorageOfUniqueServices.get('WindowOriginService').origin +
      StorageOfUniqueServices.get('APIConstants').URL_FOLDERS +
      `/${folderId}/folderAssignments`
  );

  const requestData = {
    testCasesIds,
  };

  return axios.post(url, requestData, {
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

const deleteTestCasesFromFolder = async (
  testCasesInFolderIds: Array<number | string>,
  folderId: number
): Promise<AxiosResponse<Array<TestCaseInFolder>>> => {
  const url = await StorageOfUniqueServices.get(
    'AuthenticateService'
  ).authenticateByJWT(
    StorageOfUniqueServices.get('WindowOriginService').origin +
      StorageOfUniqueServices.get('APIConstants').URL_FOLDERS +
      `/${folderId}/folderAssignments`
  );

  const data = {
    testCasesInFolderIds,
  };

  return axios.delete(url, {
    headers: {
      'Content-Type': 'application/json',
    },
    data,
  });
};

const parseFolderGetDtos = (
  folderGetDtos: Array<FolderGetDTO>
): Array<Folder> => {
  return folderGetDtos.map(parseFolderGetDto);
};

const searchFolders = async (
  name: string
): Promise<Array<FolderWithParent>> => {
  const url = await StorageOfUniqueServices.get(
    'AuthenticateService'
  ).authenticateByJWT(
    StorageOfUniqueServices.get('WindowOriginService').origin +
      StorageOfUniqueServices.get('APIConstants').URL_FOLDERS,
    [`name=${name}`]
  );

  const { data } = await axios.get<Array<FolderGetDTO>>(url, {
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return parseFolderWithParentGetDtos(data);
};

const parseFolderGetDto = (folderGetDto: FolderGetDTO): Folder => {
  const { parent: _parent, subfolders, ...rest } = folderGetDto;
  return {
    ...rest,
    subfoldersIds: subfolders.map((subfolder) => subfolder.id),
  };
};

const parseFolderWithParentGetDto = (
  folderGetDto: FolderGetDTO
): FolderWithParent => {
  const { subfolders, ...rest } = folderGetDto;
  return {
    ...rest,
    subfoldersIds: subfolders.map((subfolder) => subfolder.id),
  };
};

const parseFolderWithParentGetDtos = (
  folderGetDtos: Array<FolderGetDTO>
): Array<FolderWithParent> => {
  return folderGetDtos.map(parseFolderWithParentGetDto);
};

export default {
  createFolder,
  deleteFolder,
  addTestCasesToFolder,
  deleteTestCasesFromFolder,
  searchFolders,
  getRootFolders,
  getFolder,
  updateFolder,
};
